import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export interface WhatsAppNotificationSettings {
  whatsappNumber: string | null;
  notifyDiscount: boolean;
  notifyCancellation: boolean;
  notifyLowStock: boolean;
}

export function useWhatsAppNotificationSettings() {
  const { activeRestaurant, refetch } = useRestaurantContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [settings, setSettings] = useState<WhatsAppNotificationSettings>({
    whatsappNumber: null,
    notifyDiscount: true,
    notifyCancellation: true,
    notifyLowStock: true,
  });

  useEffect(() => {
    if (activeRestaurant) {
      setSettings({
        whatsappNumber: activeRestaurant.whatsapp_number || null,
        notifyDiscount: (activeRestaurant as any).whatsapp_notify_discount ?? true,
        notifyCancellation: (activeRestaurant as any).whatsapp_notify_cancellation ?? true,
        notifyLowStock: (activeRestaurant as any).whatsapp_notify_low_stock ?? true,
      });
    }
  }, [activeRestaurant]);

  const updateSettings = useCallback(async (newSettings: Partial<WhatsAppNotificationSettings>) => {
    if (!activeRestaurant?.id) return false;

    setIsUpdating(true);
    try {
      const updateData: Record<string, unknown> = {};
      
      if (newSettings.whatsappNumber !== undefined) {
        updateData.whatsapp_number = newSettings.whatsappNumber;
      }
      if (newSettings.notifyDiscount !== undefined) {
        updateData.whatsapp_notify_discount = newSettings.notifyDiscount;
      }
      if (newSettings.notifyCancellation !== undefined) {
        updateData.whatsapp_notify_cancellation = newSettings.notifyCancellation;
      }
      if (newSettings.notifyLowStock !== undefined) {
        updateData.whatsapp_notify_low_stock = newSettings.notifyLowStock;
      }

      const { error } = await supabase
        .from("restaurants")
        .update(updateData)
        .eq("id", activeRestaurant.id);

      if (error) throw error;

      setSettings(prev => ({ ...prev, ...newSettings }));
      await refetch();
      toast.success("Configurações de notificação salvas!");
      return true;
    } catch (error) {
      console.error("Error updating notification settings:", error);
      toast.error("Erro ao salvar configurações");
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [activeRestaurant?.id, refetch]);

  const isNotificationEnabled = useCallback((type: 'discount' | 'cancellation' | 'stock_low'): boolean => {
    switch (type) {
      case 'discount':
        return settings.notifyDiscount && !!settings.whatsappNumber;
      case 'cancellation':
        return settings.notifyCancellation && !!settings.whatsappNumber;
      case 'stock_low':
        return settings.notifyLowStock && !!settings.whatsappNumber;
      default:
        return false;
    }
  }, [settings]);

  return {
    settings,
    isLoading,
    isUpdating,
    updateSettings,
    isNotificationEnabled,
  };
}
